import { ListItemCardMobile } from "@components/common/mobile/list-item-card-mobile";
import { MobileActionBar } from "@components/common/mobile/mobile-action-bar";
import { ProjectsSort } from "@components/projects/projects-sort";
import { PaginatedProjects, SdbProject } from "@custom-types/project-types";
import { CreateProjectDialog } from "@pages/projects/create-project-dialog";
import { useProjectMenuOptionsMobile } from "@pages/projects/use-project-menu-options-mobile";
import {
  defaultProjectThumbnail,
  getProjectModifiedElapsed,
} from "@utils/project-utils";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import {
  customColumnMobileConfig,
  VIRTUAL_GRID_OFFSET_MOBILE,
} from "@pages/projects/projects-utils";
import { SkeletonListItemCard } from "@components/common/mobile/skeleton-list-item-card";
import { Box, Divider } from "@mui/material";
import { generateProjectDetailsRoute } from "@router/router-helper";

type Props = Pick<
  PaginatedProjects,
  | "projects"
  | "sortBy"
  | "setSortBy"
  | "projectArchivingState"
  | "loadMoreProjects"
> & {
  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;
};

/**
 * Mobile-specific view for projects list with action bar (search, sort, export) and card layout.
 */
export function ProjectsMobileView({
  projects,
  sortBy,
  setSortBy,
  isLoading,
  projectArchivingState,
  loadMoreProjects,
}: Props): JSX.Element | null {
  const menuItems = useProjectMenuOptionsMobile();

  const selectedEntities = useAppSelector(selectedEntitiesSelector("projects"));
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);

  const isAnyRowSelected = selectedEntities.length !== 0;

  if (!selectedSdbCompany) {
    return null;
  }

  return (
    <>
      <MobileActionBar
        renderSort={() => (
          <ProjectsSort
            key="projects-sort"
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        )}
        renderExport={() => (
          <BulkExportProjects
            companyId={selectedSdbCompany.id}
            selectedProjects={isAnyRowSelected ? selectedEntities : projects}
            subjectType={"workspace"}
            entityName={selectedSdbCompany.name}
            projectStatus={projectArchivingState}
            areEntitiesSelected={isAnyRowSelected}
          />
        )}
      />

      <VirtualizedGrid
        // The `key` here forces React to remount the grid whenever `sortBy` changes.
        // This ensures that the scroll position resets and the grid re-renders from the first item
        key={sortBy}
        items={projects}
        isLoading={isLoading}
        loadMoreItems={loadMoreProjects}
        renderSkeletonItem={(index) => (
          <Box key={index}>
            <SkeletonListItemCard />
          </Box>
        )}
        height={`calc(100vh - ${VIRTUAL_GRID_OFFSET_MOBILE}px)`}
        columnConfig={customColumnMobileConfig}
        estimateSize={112}
        renderItem={(project, index) => (
          <Box key={project.id}>
            {index !== 0 && <Divider />}

            <ListItemCardMobile<SdbProject>
              item={project}
              image={project.thumbnailUrl ?? defaultProjectThumbnail}
              title={project.name}
              href={generateProjectDetailsRoute(selectedSdbCompany.id, project.id)}
              subtitle={`Updated: ${getProjectModifiedElapsed(project)}`}
              secondaryText={project.accessLevel}
              menuItems={menuItems}
            />
          </Box>
        )}
        renderFooterAction={() => <CreateProjectDialog />}
      />
    </>
  );
}
